import React, { useState, useEffect } from 'react';
import { Switch } from '@headlessui/react';
import LogoAmex from 'assets/logo-amex.png';
import LogoApplePay from 'assets/logo-applepay.png';
import LogoGPay from 'assets/logo-gpay.png';
import LogoMastercard from 'assets/logo-mastercard.png';
import LogoVisa from 'assets/logo-visa.png';
import useAuthState from '../global-states/useAuthState';
import db from '../firebase-logic/firebase';
import toast from 'react-hot-toast';
import { StripeDialog } from './StripeDialog';
import { StripeAPI } from 'api/stripe';
import { useTranslation } from 'react-i18next';

type StripeAccountInformation = {
  id: string;
  details_submitted: boolean;
  charges_enabled: boolean;
  payouts_enabled: boolean;
};

const isStripeOnboardingUrl = (value: unknown) => {
  if (typeof value !== 'string') return false;

  try {
    const url = new URL(value);
    return url.protocol === 'https:' && url.hostname === 'connect.stripe.com';
  } catch {
    return false;
  }
};

export const CardPaymentSetting: React.FC = () => {
  const [active, setActive] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  const [stripeAccountInformation, setStripeAccountInformation] =
    useState<StripeAccountInformation>();
  const auth: any = useAuthState.getState();
  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const { t } = useTranslation(['settings']);

  const getPaymentSettingsAsync = async () => {
    setIsLoading(true);
    try {
      const docFromPath = await db.collection('companies').doc(auth.user).get();
      const doc = docFromPath.data();
      if (doc != null) {
        const paymentSettings = doc.paymentSettings || {};
        setActive(Boolean(paymentSettings.cardPayment));
        const storedStripeAccountId =
          typeof paymentSettings.stripeAccount === 'string'
            ? paymentSettings.stripeAccount
            : null;
        setStripeAccountId(storedStripeAccountId);

        if (storedStripeAccountId) {
          const accountInformationRequest =
            await StripeAPI.getAccountInformation(auth.user);
          if (accountInformationRequest.ok) {
            const accountInformationResponse =
              await accountInformationRequest.json();
            setStripeAccountInformation(accountInformationResponse.data);
          } else {
            console.error(
              'Stripe account lookup failed',
              accountInformationRequest.status
            );
            toast.error(t('stripeconnect.error'));
          }
        }

        return storedStripeAccountId;
      }
    } catch {
      console.error('Unable to load Stripe payment settings');
      toast.error(t('stripeconnect.error'));
    } finally {
      setIsLoading(false);
    }

    return null;
  };

  const startStripeOnboarding = async (createAccount: boolean) => {
    setIsLoading(true);
    try {
      if (createAccount) {
        const connectedAccount = await StripeAPI.createConnectedAccount(
          auth.user
        );
        if (!connectedAccount.ok) {
          console.error(
            'Stripe account creation failed',
            connectedAccount.status
          );
          toast.error(t('stripeconnect.creation_error'));
          return;
        }

        const accountData = await connectedAccount.json();
        if (typeof accountData.id !== 'string') {
          throw new Error('Stripe account response did not include an ID');
        }
        setStripeAccountId(accountData.id);
        setStripeAccountInformation(accountData);
      }

      const accountLink = await StripeAPI.createAccountLink(auth.user);
      if (!accountLink.ok) {
        console.error('Stripe account link failed', accountLink.status);
        toast.error(t('redirection_to_onboarding.error'));
        return;
      }

      const accountLinkData = await accountLink.json();
      if (!isStripeOnboardingUrl(accountLinkData.url)) {
        throw new Error('Stripe returned an invalid onboarding URL');
      }

      toast.success(t('redirection_to_onboarding'));
      window.location.assign(accountLinkData.url);
    } catch {
      console.error('Unable to start Stripe onboarding');
      toast.error(t('redirection_to_onboarding.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const initializeStripeSettings = async () => {
      const storedStripeAccountId = await getPaymentSettingsAsync();
      if (cancelled) return;

      const currentUrl = new URL(window.location.href);
      const stripeReturnState = currentUrl.searchParams.get('stripe');
      if (stripeReturnState !== 'refresh' && stripeReturnState !== 'return') {
        return;
      }

      currentUrl.searchParams.delete('stripe');
      window.history.replaceState(
        {},
        '',
        `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`
      );

      if (stripeReturnState === 'refresh' && storedStripeAccountId) {
        await startStripeOnboarding(false);
      }
    };

    void initializeStripeSettings();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className='w-full bg-white p-4 rounded-md max-w-3xl mx-auto bg-red mt-8 cursor-pointer'>
      <StripeDialog
        dialogIsOpen={dialogIsOpen}
        onClose={() => setDialogIsOpen(false)}
        isLoading={isLoading}
        hasAccount={Boolean(stripeAccountId)}
        hasCompletedOnboarding={
          stripeAccountInformation != null &&
          stripeAccountInformation!['details_submitted'] &&
          stripeAccountInformation!['charges_enabled'] &&
          stripeAccountInformation!['payouts_enabled']
        }
        onCreateAccount={() => startStripeOnboarding(true)}
        onCreateAccountLink={() => startStripeOnboarding(false)}
      />
      <div className='w-full flex justify-between'>
        <h2 className='font-medium text-lg'>
          {t('acceptcardpayments.labels')}
        </h2>
        {isLoading ? (
          <div className='loader ease-linear rounded-full border-4 border-t-4 border-gray-200 h-6 w-6'></div>
        ) : (
          <Switch
            checked={active}
            onChange={() => {
              if (
                stripeAccountInformation &&
                stripeAccountInformation['details_submitted'] &&
                stripeAccountInformation['charges_enabled'] &&
                stripeAccountInformation['payouts_enabled']
              ) {
                db.collection('companies')
                  .doc(auth.user)
                  .update({ 'paymentSettings.cardPayment': !active });
                setActive(!active);
                toast.success(t('stripeactivation.success'));
              } else {
                toast.error(t('stripeactivation.failure'));
              }
            }}
            className={`${
              active ? 'bg-droov' : 'bg-gray-200'
            } relative inline-flex items-center h-6 rounded-full w-11`}
          >
            <span
              className={`${
                active ? 'translate-x-6' : 'translate-x-1'
              } inline-block w-4 h-4 transform bg-white rounded-full`}
            />
          </Switch>
        )}
      </div>
      <p className='droov_text_description_light mr-11'>
        {t('acceptcardpayments.text')}
      </p>
      <div className='flex flex-col md:flex-row justify-start gap-8 items-center my-4'>
        <img src={LogoMastercard} alt='Mastercard' className='h-7' />
        <img src={LogoVisa} alt='Visa' className='h-7' />
        <img src={LogoAmex} alt='American Express' className='h-7' />
        <img src={LogoApplePay} alt='Apple Pay' className='h-10' />
        <img src={LogoGPay} alt='Google Pay' className='h-11' />
      </div>
      {!isLoading && (
        <div className='flex justify-between items-center'>
          <label
            className={`italic text-base ${
              stripeAccountInformation &&
              stripeAccountInformation['details_submitted'] &&
              stripeAccountInformation['charges_enabled'] &&
              stripeAccountInformation['payouts_enabled']
                ? 'text-green-400'
                : 'text-red-400'
            }`}
          >
            &#8226;{' '}
            {stripeAccountInformation &&
            stripeAccountInformation['details_submitted'] &&
            stripeAccountInformation['charges_enabled'] &&
            stripeAccountInformation['payouts_enabled']
              ? t('connected.label')
              : t('notconnected.label')}
          </label>
          <button
            onClick={() => setDialogIsOpen(true)}
            className='relative_add_button h-10'
          >
            {t('connectstripe.button')}
          </button>

          {/*
          <hr />
          // TODO: comment out. This is for testing purposes only //
          <button
            onClick={() => addAppleDomain()}
            className='relative_add_button h-10'
          >
            ADD DOMAIN
          </button>
          */}
        </div>
      )}
    </div>
  );
};
